const POSITION_PREFIX = "Positionen ergänzt:";
/** @deprecated Legacy-Prefix – weiterhin beim Lesen unterstützen. */
const ARTICLE_PREFIX = "Artikel ergänzt:";

function findPositionSummaryIndex(text: string): { index: number; prefix: string } | null {
  const positionIndex = text.indexOf(POSITION_PREFIX);
  if (positionIndex >= 0) return { index: positionIndex, prefix: POSITION_PREFIX };
  const articleIndex = text.indexOf(ARTICLE_PREFIX);
  if (articleIndex >= 0) return { index: articleIndex, prefix: ARTICLE_PREFIX };
  return null;
}

/** Hinterlegte Artikel/Leistungen aus Antworttext extrahieren. */
export function parseAnswerArticles(answerText: string | null | undefined): string[] {
  const text = String(answerText || "").trim();
  if (!text) return [];

  const hit = findPositionSummaryIndex(text);
  if (!hit) return [];
  return text
    .slice(hit.index + hit.prefix.length)
    .split(";")
    .map((part) => part.trim())
    .filter(Boolean);
}

/** Antworttext ohne Positions-Block für die reine Textantwort. */
export function answerTextWithoutArticles(answerText: string | null | undefined): string | null {
  const text = String(answerText || "").trim();
  if (!text) return null;

  const hit = findPositionSummaryIndex(text);
  if (!hit) return text;

  const before = text.slice(0, hit.index).trim();
  return before || null;
}

/** Antworttext inkl. angehängter Artikel/Leistungen (beim Speichern). */
export function buildBillingAnswerText(
  answer: string,
  articleLines: string[],
): string {
  const trimmed = answer.trim();
  const summary = articleLines.map((line) => line.trim()).filter(Boolean).join("; ");
  if (summary && trimmed) return `${trimmed}\n\n${POSITION_PREFIX} ${summary}`;
  if (trimmed) return trimmed;
  if (summary) return `${POSITION_PREFIX} ${summary}`;
  return "";
}

export function parseActionLogDetails(raw: unknown): Record<string, unknown> | null {
  if (!raw) return null;
  if (typeof raw === "object" && raw !== null) return raw as Record<string, unknown>;
  if (typeof raw !== "string") return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    return typeof parsed === "object" && parsed !== null ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

export function resolveAnswerArticleLines(input: {
  answerText: string | null | undefined;
  respondedAt?: string | null;
  materials: Array<{ id?: number | null; text?: string | null; menge?: number | null; einheit?: string | null; hero_nr?: string | null }>;
  actionLog: Array<{ action?: string; created_at?: string; details?: unknown }>;
  formatMaterialLine: (material: {
    text?: string | null;
    menge?: number | null;
    einheit?: string | null;
    hero_nr?: string | null;
  }) => string | null;
}): string[] {
  const fromText = parseAnswerArticles(input.answerText);
  if (fromText.length > 0) return fromText;

  if (!input.respondedAt) return [];

  const responseTime = new Date(input.respondedAt).getTime();
  if (Number.isNaN(responseTime)) return [];

  const windowMs = 10 * 60 * 1000;
  const materialIds = new Set<number>();

  for (const entry of input.actionLog) {
    if (entry.action !== "material_appended") continue;
    const entryTime = new Date(entry.created_at || "").getTime();
    if (Number.isNaN(entryTime) || Math.abs(entryTime - responseTime) > windowMs) continue;

    const details = parseActionLogDetails(entry.details);
    const ids = details?.material_position_ids;
    if (!Array.isArray(ids)) continue;
    for (const id of ids) {
      const num = Number(id);
      if (Number.isFinite(num)) materialIds.add(num);
    }
  }

  if (materialIds.size === 0) return [];

  return input.materials
    .filter((material) => material.id != null && materialIds.has(Number(material.id)))
    .map((material) => input.formatMaterialLine(material))
    .filter((line): line is string => Boolean(line));
}

export function formatBillingPrice(value: unknown): string {
  if (value == null || value === "") return "";
  const num = Number(String(value).replace(",", "."));
  if (Number.isFinite(num)) {
    return `${num.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  }
  return `${String(value)} €`;
}

export function formatBillingDateTime(value?: string | null): string {
  const parsed = new Date(value || "");
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
